import type { ClientRequestType, ClientRequestStatus } from "@/types/client-requests"
import type { TranslationKey } from "@/translations"
import type { ParentApp } from "@/contexts/parent-app-context"

export type MessageEntityType = 'ClientRequest' | 'Request' | 'OnboardingRequest'

export function buildOnboardingPath(parentApp: ParentApp, requestIdOrCreate: string, status?: ClientRequestStatus): string {
  if (parentApp === 'Portal') {
    if (status && status !== 'Draft') {
      return `/requests/${requestIdOrCreate}`
    }
    return `/requests/onboarding/${requestIdOrCreate}`
  }
  return `/next/mfe/sync/requests/onboarding/${requestIdOrCreate}`
}

export function getMessageEntityType(
  requestType: ClientRequestType | undefined,
  version: number | undefined
): MessageEntityType {
  if (requestType === 'Onboarding') {
    return version === 1 ? 'OnboardingRequest' : 'Request'
  }
  return 'ClientRequest'
}

export interface ClientRequestAction {
  id: string
  labelKey: TranslationKey
  iconName: string
}

export type ClientRequestNavigationMessage = { action: 'NAVIGATE'; route: string; openInNewTab?: boolean }

const PRE_ACCEPTED_STATUSES = new Set<ClientRequestStatus>([
  'RequestInitiated', 'Draft', 'VerifyingWorker', 'ReviewWorkerInfo', 'WorkerInfoReviewed',
])

const PRE_ACCEPTED_OR_ON_HOLD_STATUSES = new Set<ClientRequestStatus>([
  'RequestInitiated', 'Draft', 'VerifyingWorker', 'ReviewWorkerInfo', 'WorkerInfoReviewed', 'OnHold',
])

const PRE_VERIFICATION_STATUSES = new Set<ClientRequestStatus>([
  'VerifyingWorker', 'ReviewWorkerInfo', 'WorkerInfoReviewed',
])

export function getAvailableActions(
  requestType: ClientRequestType,
  status: ClientRequestStatus,
  requestId: string
): ClientRequestAction[] {
  const actions: ClientRequestAction[] = []

  if (requestType === 'Onboarding') {
    if (status === 'RequestInitiated' || status === 'Draft') {
      actions.push({
        id: 'startPreVerification',
        labelKey: 'actionStartPreVerification',
        iconName: 'Play',
      })
    }

    if (PRE_ACCEPTED_OR_ON_HOLD_STATUSES.has(status)) {
      actions.push({
        id: 'proceedToOnboarding',
        labelKey: 'actionProceedToOnboarding',
        iconName: 'ArrowRight',
      })
    }

    if (PRE_VERIFICATION_STATUSES.has(status) || status === 'RequestAccepted' || status === 'OnHold') {
      actions.push({
        id: 'viewPreverification',
        labelKey: 'actionViewPreverification',
        iconName: 'Eye',
      })
    }

    if (PRE_ACCEPTED_OR_ON_HOLD_STATUSES.has(status)) {
      actions.push({
        id: 'cancelRequest',
        labelKey: 'actionCancelRequest',
        iconName: 'XCircle',
      })
    }

    if (PRE_ACCEPTED_STATUSES.has(status)) {
      actions.push({
        id: 'onHold',
        labelKey: 'actionOnHold',
        iconName: 'Pause',
      })
    }
  } else {
    // Extension, Termination, General, Other, Redeployment
    if (status === 'RequestInitiated' || status === 'Draft' || status === 'OnHold') {
      actions.push({
        id: 'acknowledge',
        labelKey: 'actionAcknowledge',
        iconName: 'Check',
      })
    }

    if (PRE_ACCEPTED_OR_ON_HOLD_STATUSES.has(status)) {
      actions.push({
        id: 'cancelRequest',
        labelKey: 'actionCancelRequest',
        iconName: 'XCircle',
      })
    }

    if (PRE_ACCEPTED_STATUSES.has(status)) {
      actions.push({
        id: 'onHold',
        labelKey: 'actionOnHold',
        iconName: 'Pause',
      })
    }
  }

  return actions
}
